import { prepareTokenDeferred } from './defer';

/**
 * 检查用户是否已登录
 * @returns Promise<boolean> 用户登录状态
 */
export const isUserLogin = async () => {
    return prepareTokenDeferred.promise.then((token) => !!token).catch(() => false);
};
/**
 * 从 JWT token 中解析 session 字段
 * @param token JWT token 字符串
 * @returns session 字符串，解析失败返回 null
 */
function parseSessionFromToken(token: string): string | null {
    try {
        // 解析 JWT token (格式: header.payload.signature)
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // 解码 payload (base64url 编码)
        const base64Payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64Payload));
        return payload.session || null;
    } catch (error) {
        console.error('Failed to parse JWT token:', error);
        return null;
    }
}

/**
 * 检查用户是否为首次登录
 * @description 通过对比 JWT token 中的 session 字段与本地存储的 session 判断是否首次登录
 * @returns Promise<boolean> true 表示首次登录，false 表示非首次登录
 */
export async function isFirstLogin() {
    const passport = await prepareTokenDeferred.promise;
    const token = await passport.getAccessToken();

    if (!token) {
        return false;
    }

    const sessionFromToken = parseSessionFromToken(token);
    if (!sessionFromToken) {
        return false;
    }

    const localstorageSession = localStorage.getItem('user-session');

    if (!localstorageSession) {
        // 本地没有存储 session，是首次登录，存储当前 session
        localStorage.setItem('user-session', sessionFromToken);
        return true;
    }

    // 对比 session
    if (localstorageSession === sessionFromToken) {
        return false; // session 一致，不是首次登录
    } else {
        // session 不一致，是新的 session，更新本地存储
        localStorage.setItem('user-session', sessionFromToken);
        return true;
    }
}
/**
 * 匿名用户错误标识符
 */
const anonymousErrorSymbol = Symbol.for('$$AnonymousError');

/**
 * 匿名用户错误类
 * @description 用于标识匿名用户访问受限资源时抛出的错误
 */
export class AnonymousError extends Error {
    cls = anonymousErrorSymbol;
}

/**
 * 判断错误是否为匿��用户错误
 * @param e 待检查的错误对象
 * @returns boolean 是否为匿名用户错误
 */
export function isAnonymousError(e: any) {
    return e instanceof AnonymousError || (e?.cls === anonymousErrorSymbol);
}
